import React, { useCallback, useMemo } from 'react';
import { Box, Divider, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { capitalize, isEmpty } from 'lodash';
import { MAX_DROPDOWN_OPTIONS, PICK_ANSWER_TYPE, QUESTION_TYPE_CHOICES } from 'common/constants/firms';
import { useTasks } from 'services/hooks';
import { SubQuestion } from 'services/hooks/useRequestDocsAndQuestions/types';
import AddSubQuestionButton from './AddSubQuestionButton';
import DropdownOptions from './DropdownOptions';
import ModalItemBox from './ModalItemBox';
import { StyledRadio } from './styles';
import { QuestionContentProps } from './types';

const QuestionContent = ({
  formValues,
  setFormValues,
  setIsMaxCharactersError,
  dropdownTypeValue,
  isEdit,
}: QuestionContentProps) => {
  const defaultAnswerTypeValue = QUESTION_TYPE_CHOICES[0].value;
  const subQuestion = formValues.children_questions[0] || { name: '', description: '' };
  const isMaxOptions = formValues.options.length >= MAX_DROPDOWN_OPTIONS;
  const { getQuestionOptions } = useTasks();
  const questionOptions = useMemo(() => getQuestionOptions(), [getQuestionOptions]);

  const formattedOptions = useMemo(() => {
    if (Array.isArray(questionOptions)) {
      return questionOptions;
    }
    if (Array.isArray(Object.values(questionOptions))) {
      return Object.values(questionOptions);
    }
    return [];
  }, [questionOptions]);

  const handleInputSubQuestionChange = useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const newSubQuestions = [...formValues.children_questions];
      const key = e.target.name as keyof SubQuestion;
      newSubQuestions[index] = {
        ...newSubQuestions[index],
        [key]: e.target.value,
      };

      setFormValues({
        ...formValues,
        children_questions: newSubQuestions,
      });
    },
    [formValues, setFormValues]
  );

  const handleAnswerTypeClick = useCallback(
    (value: string) => {
      setFormValues({
        ...formValues,
        question_type: Number(value),
      });
    },
    [setFormValues, formValues]
  );

  return (
    <>
      <Box mb={1}>
        <Typography variant="body1" gutterBottom style={{ fontWeight: 700 }}>
          {PICK_ANSWER_TYPE}
        </Typography>
        <FormControl>
          <RadioGroup
            row
            aria-labelledby="pick-answer-type-radio-label"
            name="radio-buttons-group"
            value={formValues.question_type ?? defaultAnswerTypeValue}
            onChange={event => handleAnswerTypeClick(event.target.value)}>
            {QUESTION_TYPE_CHOICES.map(type => (
              <FormControlLabel
                key={type.value}
                value={type.value}
                control={<StyledRadio />}
                label={capitalize(type.label)}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      {formValues.question_type === dropdownTypeValue && (
        <Box>
          <DropdownOptions
            formValues={formValues}
            setFormValues={setFormValues}
            isMaxOptions={isMaxOptions}
            questionOptions={formattedOptions}
          />
        </Box>
      )}

      <Divider style={{ marginTop: 8 }} />

      <AddSubQuestionButton
        setFormValues={setFormValues}
        formValues={formValues}
        isEdit={isEdit}
        subQuestion={subQuestion}
      />
      {!isEmpty(formValues.children_questions) && (
        <ModalItemBox
          key={subQuestion.question_type ?? 0}
          index={0}
          item={subQuestion}
          handleInputChange={handleInputSubQuestionChange}
          setIsMaxCharactersError={setIsMaxCharactersError}
          formValues={formValues}
          setFormValues={setFormValues}
          attribute="children_questions"
          inputLabel="Sub-question"
          additionalInputs={[
            {
              id: 'description',
              name: 'description',
              label: 'Description',
              placeholder: 'Description',
              maxCharacters: 500,
            },
          ]}
        />
      )}
    </>
  );
};

export default QuestionContent;
